import { System } from 'src/lib/utils';
import { EntityManager, PetEntity } from '../logic/EntityManager';
import platform, { analytics } from '@play-co/gcinstant';
import { Conversation } from 'src/replicant/conversation';
import StateObserver from '../../StateObserver';
import { closePopup, openPopupPromise } from '../../lib/popups/popupOpenClose';
import { conversationActions } from './conversation.redux';
import { createPersistentEmitter } from '../../lib/Emitter';
import { State } from '../../state';
import PopupMonitor from '../logic/PopupMonitor';
import {
  ConversationalScript,
  DescriptiveScript,
  DialogScenes,
  Script,
  SelectionalScript,
} from 'src/replicant/conversation/conservation.ruleset';
import { captureGenericError } from 'src/lib/sentry';
import { Widen } from 'src/replicant/utils/types';
import { Pet } from 'src/replicant/buruburu/pet';
import { getDialogStartEvent } from 'src/replicant/conversation/conversation.analytics';
import { Visit } from 'src/replicant/visit';
import statePromise from '../../lib/statePromise';
import { getFriendsPet, getPet } from '../pet';
import { VisitRecord } from '../../replicant/visit/record';
import { weightedRandom } from 'src/replicant/utils/random';
import { getDynamicTestBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { clearTempVisibleStats, setTempVisibleStats } from 'src/state/ui';
import { getTutorialController } from '../tutorial/TutorialController';
import { ascensionSequence } from '../ascension/ascension.sequence';
import { Tutorial } from 'src/replicant/buruburu/tutorial';
import { isSceneEntered } from 'src/lib/stateUtils';

export function conversationSystem(Conversation: Conversation): System {
  const isInConversationScene = () =>
    isSceneEntered('livingRoom') || isSceneEntered('friendsLivingRoom');

  const isPetIdle = (id: string) => {
    const { care, pet, conversation } = StateObserver.getState();
    return (
      !care.currentCareAction &&
      !conversation[id]?.activeDialog &&
      !pet[id].action
    );
  };

  const handlePetTapped = (id: string) => {
    const { user, pet } = StateObserver.getState();
    const { isTutorialStepComplete } = Tutorial.getters(user);
    if (
      !isInConversationScene() ||
      !isPetIdle(id) ||
      !isTutorialStepComplete('basicGameplay')
    ) {
      return;
    }

    const { getValidDialogs } = Conversation.getters(user);

    const {
      ruleset: { getPetState: getRemotePetState },
      getters,
    } = Pet;
    const { getPetState, hasStatusEffect } = getters(user);

    const isLocalPet = id === platform.playerID;
    const isVisiting = Visit.getters(user).isVisiting();

    let petState = isLocalPet
      ? // Use local state to get pet state.
        getPetState(StateObserver.now(), isVisiting)
      : // Use the snapshot data we have for remote players.
        getRemotePetState(pet[id].attributes, pet[id].statusEffects, true);

    let dialogScene: DialogScenes;
    if (!isVisiting && hasStatusEffect('hadVisitor', StateObserver.now())) {
      //If pet dead handle a visitor as if the pet has already played had_visitor dialog
      if (hasStatusEffect('dead', StateObserver.now())) {
        void StateObserver.invoke.acknowledgeVisit();
        dialogScene = isLocalPet ? 'normal_chat' : 'friend_chat';
      } else {
        dialogScene = 'had_visitor';
      }
    } else {
      dialogScene = isLocalPet ? 'normal_chat' : 'friend_chat';
    }
    const dialogOptions = getValidDialogs(
      dialogScene,
      pet[id].species,
      petState,
      isLocalPet
        ? user.conversation.answers
        : user.visit.visitSession?.answers ?? {},
    );

    if (!dialogOptions || dialogOptions.length === 0) {
      captureGenericError('Dialog not found', new Error('Dialog not found'));
      return;
    }

    // Prioritize dialogs with required answers.
    const weights = dialogOptions.map((dialog) =>
      !isLocalPet && dialog.req_answers ? 100 : 1,
    );
    const dialog = dialogOptions[weightedRandom(weights, Math.random)];

    StateObserver.dispatch(
      conversationActions.setActiveDialog({ id, payload: dialog.id }),
    );
    StateObserver.dispatch(setTempVisibleStats(id));
  };

  let interactablePets: string[] = [];

  const handlePetAddedOrRemoved = async (ids: string[]) => {
    // Add interaction.
    ids.forEach((id) => {
      if (interactablePets.includes(id)) return;
      EntityManager.createOrGetEntity(
        EntityManager.getEntityId('pet', id),
      ).on(PetEntity.TapEvent, () => handlePetTapped(id));
    });

    // Remove interaction.
    interactablePets.forEach((id) => {
      if (ids.includes(id)) return;
      EntityManager.getEntity(
        EntityManager.getEntityId('pet', id),
      )?.removeAllListeners(PetEntity.TapEvent);
    });

    interactablePets = ids;
  };

  const handleDialogUpdated = async (
    dialogs: {
      id: string;
      activeDialog: State['conversation'][string]['activeDialog'];
    }[],
  ) => {
    for (const { id, activeDialog } of dialogs) {
      const { user, pet, level } = StateObserver.getState();
      const { getTimesSeen } = Conversation.getters(user);
      const { getVisitedCount, getVisitsCount } = VisitRecord.getters;

      if (activeDialog !== null) {
        const { friendId } = user.visit.visitSession ?? {};
        const friendAttributes = pet[friendId]?.attributes;
        const friendAppearance = pet[friendId];
        const friendLevel = level[friendId];

        const { eventType, eventProperties } = getDialogStartEvent({
          dialog_id: activeDialog,
          times_seen: getTimesSeen(activeDialog) + 1,
          withFriends: !!friendId,
          friendID: friendId,
          friendPetID: friendAppearance?.species,
          friendPetStomach: friendAttributes?.stomach,
          friendPetLove: friendAttributes?.love,
          friendDirtiness: friendAttributes?.dirtiness,
          friendUrine: friendAttributes?.urine,
          friendStress:
            friendAttributes && Pet.ruleset.getStressValue(friendAttributes),
          friendToothAcidity: friendAttributes?.toothAcidity,
          friendAge: friendAttributes?.age,
          friendPlayerLevel: friendLevel?.current,
          visits: friendId && getVisitsCount(user, friendId),
          visited: friendId && getVisitedCount(user, friendId),
        });
        analytics.pushEvent(eventType, eventProperties);
        await StateObserver.invoke.incrementDialogTimesSeen(activeDialog);
      }

      const dialog = Conversation.ruleset.dialogs[activeDialog];
      StateObserver.dispatch(
        conversationActions.setActiveScript({
          id,
          payload: dialog?.script_id ?? null,
        }),
      );

      // Handle pet evolving.
      if (dialog?.pet_states?.includes('evolving')) {
        void getPet().handleEvolving();
      }

      // Handle pet death
      if (dialog?.pet_states?.includes('dead')) {
        void ascensionSequence();
      }

      // Acknowledge visit when closing had_visitor dialog
      if (dialog?.scenes?.includes('had_visitor')) {
        void statePromise(
          ({ conversation }) => !conversation[id].activeDialog,
        ).then(() => StateObserver.invoke.acknowledgeVisit());
      }
    }
  };

  const handleScriptUpdated = async (
    scripts: {
      id: string;
      activeScript: State['conversation'][string]['activeScript'];
    }[],
  ) => {
    if (PopupMonitor.isOpen('popupConversationScript')) {
      closePopup('popupConversationScript');
    }

    const tutorial = getTutorialController();

    await tutorial.handleEvent('conversationStarted');

    for (const { id, activeScript } of scripts) {
      const script = Conversation.ruleset.getScript(activeScript);
      if (!script) {
        continue;
      }

      const entityId = EntityManager.getEntityId('pet', id);
      const pet =
        entityId === EntityManager.createOrGetPlayerPet().entityId
          ? getPet()
          : getFriendsPet();

      const { move_to: moveTo } = script;

      if (moveTo) {
        const { nextScene: scene } = StateObserver.getState().ui.transition;

        if (scene !== 'livingRoom' && scene !== 'friendsLivingRoom') {
          throw new Error('Cannot start script, scene not supported');
        }

        await pet.moveTo({
          scene,
          location: moveTo,
        });
      }

      const {
        scriptIsConversational,
        scriptIsDescriptive,
        scriptIsSelectional,
      } = Conversation.ruleset;
      let nextScriptId: Script['id'] | undefined;
      if (scriptIsConversational(script)) {
        nextScriptId = await showConversationalScript(script);
      } else if (scriptIsSelectional(script)) {
        nextScriptId = await showSelectionalScript(script);
      } else if (scriptIsDescriptive(script)) {
        nextScriptId = await showDescriptiveScript(script);
      }

      if (!nextScriptId) {
        StateObserver.dispatch(
          conversationActions.setActiveDialog({ id, payload: null }),
        );
        StateObserver.dispatch(clearTempVisibleStats());
      } else {
        StateObserver.dispatch(
          conversationActions.setActiveScript({ id, payload: nextScriptId }),
        );
      }
    }

    await tutorial.handleEvent('conversationFinished');
  };

  const interactionEmitter = createPersistentEmitter(({ conversation }) =>
    Object.keys(conversation),
  );

  const dialogEmitter = createPersistentEmitter(({ conversation }) =>
    Object.keys(conversation).map((id) => ({
      id,
      activeDialog: conversation[id].activeDialog,
    })),
  );

  const scriptEmitter = createPersistentEmitter(({ conversation }) =>
    Object.keys(conversation).map((id) => ({
      id,
      activeScript: conversation[id].activeScript,
    })),
  );

  return {
    start: () => {
      dialogEmitter.addListener(handleDialogUpdated);
      scriptEmitter.addListener(handleScriptUpdated);
      interactionEmitter.addListener(handlePetAddedOrRemoved);
    },
    stop: () => {
      dialogEmitter.removeListener(handleDialogUpdated);
      scriptEmitter.removeListener(handleScriptUpdated);
      interactionEmitter.removeListener(handlePetAddedOrRemoved);
    },
  };
}

async function showConversationalScript(
  script: Widen<Script> & ConversationalScript,
) {
  await openPopupPromise('popupConversationScript', script);

  if (script.next_script_id) {
    return script.next_script_id;
  }

  const { user } = StateObserver.getState();
  const { answers } = user.conversation;
  const { visitSession } = user.visit;

  let answerKey = script.case;
  let answer = answers[answerKey]?.key;
  if (answerKey?.includes('friend.') && visitSession) {
    answerKey = answerKey.replace('friend.', '');
    answer = visitSession.answers[answerKey]?.key;
  }

  const nextScriptKey = `next_script_id_${answer}`;
  if (nextScriptKey in script) {
    return script[nextScriptKey];
  }

  return undefined;
}

async function showSelectionalScript(
  script: Widen<Script> & SelectionalScript,
) {
  const answer = await openPopupPromise('popupSelectionalScript', script);
  const localeSuffix =
    StateObserver.getState().ui.locale === 'en' ? 'en' : 'jp';
  await StateObserver.invoke.saveConversationAnswer({
    answerVar: script.answer_var,
    key: `${answer}`,
    value: script[`option_${answer}_${localeSuffix}`],
  });

  if (script.next_script_id) {
    return script.next_script_id;
  }

  const nextScriptKey = `next_script_id_${answer}`;
  if (nextScriptKey in script) {
    return script[nextScriptKey];
  }

  const errMsg = `Next script key for answer ${answer} not found for script ${script.id}`;
  console.error(errMsg);
  captureGenericError(errMsg, null);
}

async function showDescriptiveScript(
  script: Widen<Script> & DescriptiveScript,
) {
  await openPopupPromise('popupConversationScript', script);
  const answer = await openPopupPromise('popupDescriptiveScript', script);
  await StateObserver.invoke.saveConversationAnswer({
    answerVar: script.answer_var,
    value: answer,
  });
  return script.next_script_id;
}
